import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, FileType2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { MARKETPLACE_OPTIONS, COUNTRY_OPTIONS, FIELD_LABELS } from "@/lib/marketplaces";

const EMPTY = {
  template_name: "",
  marketplace: "Amazon",
  country: "India",
  is_default: true,
  notes: "",
  status: "active",
  column_mapping: {},
};

export default function Templates() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/marketplace-templates").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.template_name) return toast.error("Template name required");
    try {
      const payload = { ...form };
      if (editing) await api.put(`/marketplace-templates/${editing.id}`, payload);
      else await api.post("/marketplace-templates", payload);
      toast.success(editing ? "Template updated" : "Template created");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const onEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t, column_mapping: { ...t.column_mapping } }); setOpen(true); };

  const onDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.template_name}"?`)) return;
    try { await api.delete(`/marketplace-templates/${t.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(extractError(e)); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Masters</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Marketplace Templates</h1>
          <p className="mt-1 text-sm text-slate-500">
            Saved column-mapping templates per marketplace. The default template is auto-applied during weekly CSV uploads.
          </p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button data-testid="template-add-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg">
                <Plus className="h-4 w-4 mr-1" /> New template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit template" : "Create template"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <F label="Template name" required>
                  <Input data-testid="template-form-name" value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })} placeholder="e.g. Amazon India — Top Search Terms (Simple)" />
                </F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Marketplace">
                    <Select value={form.marketplace} onValueChange={(v) => setForm({ ...form, marketplace: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{MARKETPLACE_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Country">
                    <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{COUNTRY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                </div>
                <F label="Default for this marketplace">
                  <div className="flex items-center gap-3 h-9">
                    <Switch checked={!!form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} />
                    <span className="text-sm text-slate-600">{form.is_default ? "Yes — auto-applied during uploads" : "Not default"}</span>
                  </div>
                </F>
                <F label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>

                <div className="rounded-lg border border-slate-200 p-4 space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Column mapping (system field → exact CSV header)</div>
                  <div className="grid grid-cols-2 gap-2">
                    {FIELD_LABELS.map(([key, label, req]) => (
                      <div key={key}>
                        <Label className="text-[11px] text-slate-500">{label}{req ? " *" : ""}</Label>
                        <Input
                          data-testid={`tpl-map-${key}`}
                          value={form.column_mapping?.[key] || ""}
                          onChange={(e) => setForm({ ...form, column_mapping: { ...form.column_mapping, [key]: e.target.value } })}
                          placeholder="CSV column name"
                          className="h-8 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="template-form-save" onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">{editing ? "Save" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-list">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500">
                  <FileType2 strokeWidth={1.5} className="h-3.5 w-3.5" />
                  {t.marketplace} · {t.country || "—"}
                </div>
                <h3 className="mt-1 text-base font-semibold text-slate-900 tracking-tight">{t.template_name}</h3>
              </div>
              {t.is_default ? (
                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 shrink-0">Default</span>
              ) : null}
            </div>
            <div className="mt-3 text-xs text-slate-500">{Object.keys(t.column_mapping || {}).length} fields mapped</div>
            {t.notes ? <p className="mt-3 text-sm text-slate-600 line-clamp-2">{t.notes}</p> : null}
            {isAdmin && (
              <div className="mt-4 flex items-center gap-2">
                <Button variant="ghost" size="sm" data-testid={`template-edit-${t.template_name}`} onClick={() => onEdit(t)}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => onDelete(t)}>Delete</Button>
              </div>
            )}
          </div>
        ))}
        {!items.length && <div className="text-sm text-slate-500">No templates yet.</div>}
      </div>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </Label>
      {children}
    </div>
  );
}
